import { Card, Flex, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { type RootState } from "../../../../store.ts"
import { type AuditLogEntry } from "../../../../types/audit-log.types.ts"
import { type PermissionLeadPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { getLeadLogHistory } from "../../api/services.ts"
import type { Lead } from "../../types/lead.types.ts"
import { LeadActivityFilters } from "./LeadActivityFilters.tsx"
import { LeadActivityTimeline } from "./LeadActivityTimeline.tsx"
import {
  NO_FILTERS,
  composeLeadActivity,
  filterLeadActivity,
  leadActivityUsers
} from "./lead-activity.ts"
import type { LeadActivityFilters as Filters } from "./lead-activity.types.ts"
import { useLeadActivityFormat } from "./use-lead-activity-format.ts"

const { Title } = Typography

interface LeadActivityTabProps {
  lead: Lead
  leadPermissions: PermissionLeadPermissions
}

/**
 * A single full-width chronological feed — Follow-Up, Appointment, Files and
 * Internal Notes used to live here as a right-side rail, but every one of
 * them already has its own home (the header, Files tab, Overview) since the
 * 2026-08-30 header/tabs redesign, so duplicating them here was the odd one
 * out rather than this page. This tab now answers exactly one question:
 * "what happened to this lead, in order."
 */
export function LeadActivityTab({
  lead,
  leadPermissions
}: LeadActivityTabProps) {
  const { t } = useTranslation()
  const { dateFormat, timezone } = useLeadActivityFormat()
  const owners = useSelector(
    (state: RootState) => state.leads?.filterOptions?.sales_manager
  )

  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)

  const canReadLog = !!leadPermissions?.features?.lead_activity_log
  const leadId = lead?.id
  const calendarEvents = lead?.lead_calendar_events

  useEffect(() => {
    if (!leadId || !canReadLog) {
      setAuditEntries([])
      return
    }

    let cancelled = false
    setLoading(true)

    getLeadLogHistory(leadId)
      .then((response) => {
        if (cancelled) return
        setAuditEntries(
          response.ok && Array.isArray(response.data)
            ? (response.data as AuditLogEntry[])
            : []
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [leadId, canReadLog, calendarEvents])

  const ownerLabels = useMemo(
    () =>
      (owners ?? []).reduce<Record<string, string>>((acc, option) => {
        if (option?.value != null)
          acc[String(option.value)] = String(option.label)
        return acc
      }, {}),
    [owners]
  )

  const entries = useMemo(
    () =>
      composeLeadActivity({
        lead,
        auditEntries,
        userLabels: ownerLabels,
        userFormat: dateFormat,
        timezone
      }),
    [lead, auditEntries, ownerLabels, dateFormat, timezone]
  )

  const visible = useMemo(
    () => filterLeadActivity(entries, filters),
    [entries, filters]
  )
  const users = useMemo(() => leadActivityUsers(entries), [entries])

  return (
    <Card size={"small"} data-testid={"lead-activity"}>
      <Flex vertical gap={"middle"}>
        <Title level={5} className={"m-0"}>
          {t("leadWorkspace.activity.timeline.title")}
        </Title>

        <LeadActivityFilters
          filters={filters}
          users={users}
          onChange={(patch) =>
            setFilters((current) => ({ ...current, ...patch }))
          }
        />

        <LeadActivityTimeline
          entries={visible}
          loading={loading}
          filtered={visible.length !== entries.length}
        />
      </Flex>
    </Card>
  )
}
